import { IAgency } from './agency';
import { ITimestampsModel } from './base';
import { IUser } from './user';

export type DIRStatus = 'DRAFT' | 'COMPLETE';

export interface IDomesticIncidentReportRecord extends ITimestampsModel {
  _id: string;
  dirId: string;
  kase?: {
    _id: string;
    number: string;
    agency?: IAgency;
  };
  agency: IAgency;
  createdBy: IUser;
  status: DIRStatus;
  isRestricted: boolean;
  hasPrint: boolean;
  caseRestrictedByUser?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  usersWithCaseAccessPopulated?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  }[];
  deleted: boolean;
  deletionInfo?: {
    deletedBy: string;
    deletedAt: string;
    reason: string;
  };
  incident?: IDomesticIncidentReport['incident'];
  victim?: IDomesticIncidentReport['victim'];
  suspect_p2?: IDomesticIncidentReport['suspect_p2'];
  victim_interview?: IDomesticIncidentReport['victim_interview'];
  suspect?: IDomesticIncidentReport['suspect'];
  witnesses_1?: IDomesticIncidentReport['witnesses_1'];
  witnesses_2?: IDomesticIncidentReport['witnesses_2'];
  witnesses_3?: IDomesticIncidentReport['witnesses_3'];
  incident_p2?: IDomesticIncidentReport['incident_p2'];
  evidence?: IDomesticIncidentReport['evidence'];
  offense?: IDomesticIncidentReport['offense'];
  prior_history?: IDomesticIncidentReport['prior_history'];
  closing_notes?: IDomesticIncidentReport['closing_notes'];
}

export const OffenseValues = [
  'Aggravated Family Offense (240.75; E Felony)',
  'Assault 2nd (120.05; D Felony)',
  'Criminal Mischief 1st (145.12; B Felony)',
  'Criminal Mischief 2nd (145.10; D Felony)',
  'Criminal Mischief 3rd (145.05; E Felony)',
  'Reckless Endangerment 1st (120.25; D Felony)',
  'Stalking 1st (120.60; D Felony)',
  'Stalking 2nd (120.55; E Felony)',
  'Strangulation 1st (121.13; C Felony)',
  'Strangulation 2nd (121.12; D Felony)',
  'Grand Larceny 3rd (155.35; D Felony)',
  'Grand Larceny 4th (155.30; E Felony)',
  'Identity Theft 1st (190.80; D Felony)',
  'Identity Theft 2nd (190.79; E Felony)',
  'Assault 1st (120.10; B Felony)',
  'Burglary 1st (140.30; B Felony)',
  'Burglary 2nd (140.25; C Felony)',
  'Burglary 3rd (140.20; D Felony)',
  'Robbery 1st (160.15; B Felony)',
  'Robbery 2nd (160.10; C Felony)',
  'Coercion 1st (135.65; D Felony)',
  'Criminal Contempt 1st (215.51; E Felony)',
  'Criminal Trespass 1st (140.17; D Felony)',
  'Endangering Welfare of a Vulnerable Elderly Person 1st (260.34; D Felony)',
  'Intimidating Victim or Witness 1st (215.17; B Felony)',
  'Intimidating Victim or Witness 2nd (215.16; D Felony)',
  'Intimidating Victim or Witness 3rd (215.15; E Felony)',
  'Menacing 1st (120.13; E Felony)',
  'Manslaughter 1st (125.20; B Felony)',
  'Manslaughter 2nd (125.15; C Felony)',
  'Murder 1st (125.27; A-I Felony)',
  'Murder 2nd (125.25; A-I Felony)',
  'Unlawful Imprisonment 1st (135.10; E Felony)',
  'Aggravated Sexual Abuse 1st (130.70; B Felony)',
  'Aggravated Sexual Abuse 2nd (130.67; C Felony)',
  'Aggravated Sexual Abuse 3rd (130.66; D Felony)',
  'Aggravated Sexual Abuse 4th (130.65-a; E Felony)',
  'Computer Tampering 1st (156.27; C Felony)',
  'Computer Tampering 2nd (156.26; D Felony)',
  'Computer Tampering 3rd (156.25; E Felony)',
  'Computer Trespass (156.10; E Felony)',
  'Criminal Possession of a Dangerous Weapon 1st (265.04; B Felony)',
  'Criminal Possession of a Weapon 2nd (265.03; C Felony)',
  'Criminal Possession of a Weapon 3rd (265.02; D Felony)',
  'Criminal Sexual Act 1st (130.50; B Felony)',
  'Criminal Sexual Act 2nd (130.45; D Felony)',
  'Criminal Sexual Act 3rd (130.40; E Felony)',
  'Criminal Tampering 1st (145.20; D Felony)',
  'Criminal Use of a Firearm 1st (265.09; B Felony)',
  'Criminal Use of a Firearm 2nd (265.08; A Misdemeanor)',
  'Criminally Negligent Homicide (125.10; E Felony)',
  'Endangering Welfare of Vulnerable Elderly Person 2nd (260.32; E Felony)',
  'Kidnapping 1st (135.25; A-I Felony)',
  'Kidnapping 2nd (135.20; B Felony)',
  'Rape 1st (130.35; B Felony)',
  'Rape 2nd (130.30; D Felony)',
  'Rape 3rd (130.25; E Felony)',
  'Sexual Abuse 1st (130.65; D Felony)',
  'Tampering with a Witness 1st (215.13; B Felony)',
  'Tampering with a Witness 2nd (215.12; D Felony)',
  'Tampering with a Witness 3rd (215.11; E Felony)',
  'Unauthorized Use of a Vehicle 1st (165.08; D Felony)',
  'Unauthorized Use of a Vehicle 2nd (165.06; E Felony)',
  'Unlawful Surveillance 2nd (250.45; E Felony)',
] as const;

export type OffenseType = (typeof OffenseValues)[keyof typeof OffenseValues];

export const physicalEffects = {
  yes: 'Yes',
  no: 'No',
  loss_of_consciousness: 'Loss of consciousness',
  urination_defecation: 'Urination/Defecation',
  red_eyes_petechiae: 'Red Eyes/Petechiae',
  sore_throat: 'Sore Throat',
  breathing_changed: 'Breathing Changed',
  difficulty_swallowing: 'Difficulty Swallowing',
};

export type PhysicalEffect = keyof typeof physicalEffects;

export const otherEvidence = {
  damaged_property: 'Damaged Property',
  videos: 'Videos',
  electronic_evidence: 'Electronic Evidence',
  other: 'Other',
};

export type OtherEvidence = keyof typeof otherEvidence;

export const affectedParties = {
  victim: 'Victim',
  children: 'Child/Children',
  pet: 'Pet',
  commit_suicide: 'Commit Suicide',
  other: 'Other',
};

export type AffectedParties = keyof typeof affectedParties;

export const emotions = {
  upset: 'Upset',
  nervous: 'Nervous',
  crying: 'Crying',
  angry: 'Angry',
  other: 'Other',
};

export type Emotions = keyof typeof emotions;

export const relationships = {
  married: 'Married',
  intimate_partner: 'Intimate Partner/Dating',
  child_of_victim_p1: 'Child of Victim (P1)',
  formerly_married: 'Formerly Married',
  former_intimate_partner: 'Former Intimate Partner',
  parent_of_victim_p1: 'Parent of victim (P1)',
  relative: 'Relative',
  other: 'Other',
};

export type Relationship = keyof typeof relationships;

export const languages = [
  'Abkhaz',
  'Afar',
  'Afrikaans',
  'Akan',
  'Albanian',
  'Amharic',
  'Arabic',
  'Aragonese',
  'Armenian',
  'Assamese',
  'Avaric',
  'Avestan',
  'Aymara',
  'Azerbaijani',
  'Bambara',
  'Bashkir',
  'Basque',
  'Belarusian',
  'Bengali',
  'Bihari',
  'Bislama',
  'Bosnian',
  'Breton',
  'Bulgarian',
  'Burmese',
  'Catalan; Valencian',
  'Chamorro',
  'Chechen',
  'Chichewa, Chewa, Nyanja',
  'Chinese',
  'Chuvash',
  'Cornish',
  'Corsican',
  'Cree',
  'Croatian',
  'Czech',
  'Danish',
  'Divehi, Dhivehi, Maldivian',
  'Dutch',
  'English',
  'Esperanto',
  'Estonian',
  'Ewe',
  'Faroese',
  'Fijian',
  'Finnish',
  'French',
  'Fula, Fulah, Pulaar, Pular',
  'Galician',
  'Georgian',
  'German',
  'Greek, Modern',
  'Guaraní',
  'Gujarati',
  'Haitian, Haitian Creole',
  'Hausa',
  'Hebrew (modern)',
  'Herero',
  'Hindi',
  'Hiri Motu',
  'Hungarian',
  'Interlingua',
  'Indonesian',
  'Interlingue',
  'Irish',
  'Igbo',
  'Inupiaq',
  'Ido',
  'Icelandic',
  'Italian',
  'Inuktitut',
  'Japanese',
  'Javanese',
  'Kalaallisut, Greenlandic',
  'Kannada',
  'Kanuri',
  'Kashmiri',
  'Kazakh',
  'Khmer',
  'Kikuyu, Gikuyu',
  'Kinyarwanda',
  'Kirghiz, Kyrgyz',
  'Komi',
  'Kongo',
  'Korean',
  'Kurdish',
  'Kwanyama, Kuanyama',
  'Latin',
  'Luxembourgish, Letzeburgesch',
  'Luganda',
  'Limburgish, Limburgan, Limburger',
  'Lingala',
  'Lao',
  'Lithuanian',
  'Luba-Katanga',
  'Latvian',
  'Manx',
  'Macedonian',
  'Malagasy',
  'Malay',
  'Malayalam',
  'Maltese',
  'Māori',
  'Marathi (Marāṭhī)',
  'Marshallese',
  'Mongolian',
  'Nauru',
  'Navajo, Navaho',
  'Norwegian Bokmål',
  'North Ndebele',
  'Nepali',
  'Ndonga',
  'Norwegian Nynorsk',
  'Norwegian',
  'Nuosu',
  'South Ndebele',
  'Occitan',
  'Ojibwe, Ojibwa',
  'Old Church Slavonic, Church Slavic, Church Slavonic, Old Bulgarian, Old Slavonic',
  'Oromo',
  'Oriya',
  'Ossetian, Ossetic',
  'Panjabi, Punjabi',
] as const;

export type Language = (typeof languages)[keyof typeof languages];

export const domesticSections = {
  incident: 'incident',
  victim: 'victim',
  suspect_p2: 'suspect_p2',
  victim_interview: 'victim_interview',
  suspect: 'suspect',
  witnesses_1: 'witnesses_1',
  witnesses_2: 'witnesses_2',
  witnesses_3: 'witnesses_3',
  incident_p2: 'incident_p2',
  evidence: 'evidence',
  offense: 'offense',
  prior_history: 'prior_history',
  closing_notes: 'closing_notes',
} as const;

export type IDomesticSectionKey =
  (typeof domesticSections)[keyof typeof domesticSections];

export interface IDomesticIncidentReport extends ITimestampsModel {
  [domesticSections.incident]: {
    agency: string;
    ori: string;
    incident: string;
    reported_date: string;
    reported_time: string;
    occured_date: string;
    occured_time: string;
    address: string;
    city_state_zip: string;
    initiated_check: string[];
    complaint: string;
    customAddress: boolean;
  };
  [domesticSections.victim]: {
    victim_name: string;
    dob: string;
    age: string;
    gender: string;
    customAddress: boolean;
    address: string;
    city_state_zip: string;
    language: Language;
    how_to_contact: string;
  };
  [domesticSections.suspect_p2]: {
    suspect_p2_name: string;
    dob: string;
    age: string;
    gender: string;
    customAddress: boolean;
    address: string;
    city_state_zip: string;
    language: Language;
    suspect_supervised_check: string[];
    suspect_P2_relationship_victim_P1_check: Relationship[];
    live_together: boolean;
    suspect_present: boolean;
    was_suspect_injured: boolean;
    possible_substance_use: boolean;
    injury_description: string;
    relative_details: string;
  };
  [domesticSections.victim_interview]: {
    emo_condition_check: Emotions[];
    first_words: string;
    gun_check: string;
    injured: boolean;
    in_pain: boolean;
    suspect_threats_to_check: AffectedParties[];
    strangulation_check: PhysicalEffect[];
    in_pain_details: string;
    injured_details: string;
    fearfull_victim: boolean;
    weapon_used: boolean;
    access_to_guns: boolean;
    visible_marks: boolean;
    fearfull_details: string;
    access_to_guns_details: string;
    suspect_threats_to_details: string;
    visible_marks_details: string;
  };
  [domesticSections.suspect]: {
    suspect_said: string;
    completed_710: boolean;
  };
  [domesticSections.witnesses_1]: {
    witnesses_1_name: string;
    dob: string;
    customAddress: boolean;
    address: string;
    city_state_zip: string;
    phone: string;
  };
  [domesticSections.witnesses_2]: {
    witnesses_2_name: string;
    dob: string;
    customAddress: boolean;
    address: string;
    city_state_zip: string;
    phone: string;
  };
  [domesticSections.witnesses_3]: {
    witnesses_3_name: string;
    dob: string;
    customAddress: boolean;
    address: string;
    city_state_zip: string;
    phone: string;
  };
  [domesticSections.incident_p2]: {
    describe_circumstances: string;
    dir_repository_checked: boolean;
    registry_checked: boolean;
    in_effect: boolean;
  };
  [domesticSections.evidence]: {
    photos_taken_check: string[];
    other_evidence_check: OtherEvidence[];
    destruction_of_property: boolean;
    photos_taken_details: string;
    other_evidence_details: string;
    destruction_of_property_details: string;
  };
  [domesticSections.offense]: {
    offense_committed?: boolean;
    was_arrested?: boolean;
    was_arrested_details?: string;
    offense_1?: OffenseType;
    offense_2?: OffenseType;
    offense_3?: OffenseType;
  };
  [domesticSections.prior_history]: {
    prior_incidents: string;
    threatened_to_kill_child: boolean;
    strangled_or_choked: boolean;
    beaten_while_pregnant: boolean;
    capable_of_killing_child: boolean;
    violently_jealous: boolean;
    increased_severity_past_months: boolean;
    agency: string;
    ori: string;
    incident: string;
    complaint: string;
  };
  [domesticSections.closing_notes]: {
    child_victim_neglect: boolean;
    is_dir_given: boolean;
    is_dir_given_details: string;
    is_rights_given: boolean;
    is_rights_given_details: string;
  };
}
